import { fetchApi } from '@/services/api-client';

import type { House } from '../houses/houses';
import type { TuitionCharge } from '../tuition/tuition';

export const STUDENTS_PATH = '/students';

export type Sex = 'male' | 'female';

export type StudentAllergy = { id: string; label: string; severe: boolean };

export type AllergySeverity = 'none' | 'restriction' | 'severe';

/** A behaviour state currently applying to the student. */
export type StudentState =
  | { kind: 'lunch_detention'; startDate: string; endDate: string }
  | { kind: 'suspension'; startDate: string; endDate: string }
  | { kind: 'sst_supervision'; startDate: string; endDate: null };

export type EnrolmentStatus = 'trial' | 'admitted' | 'enrolled' | 'left';

/** The Students list's status filter; `trial-ending` is a trial whose end date has come. */
export const STUDENT_STATUS_FILTERS = ['enrolled', 'trial', 'trial-ending', 'admitted', 'left'] as const;
export type StudentStatusFilter = (typeof STUDENT_STATUS_FILTERS)[number];

export type Student = {
  id: string;
  userId: string | null;
  fullName: string;
  nickname: string | null;
  sex: Sex | null;
  displayName: string;
  gradeLevel: number;
  /** EDP places this child pays nothing for, each worth the round's basePrice. 0 for most children. */
  edpFreePlaces: number;
  houseId: string | null;
  house: House | null;
  dateOfBirth: string | null;
  photoUrl: string;
  /** In class today: enrolled, or a trial that has started. */
  active: boolean;
  enrolmentStatus: EnrolmentStatus;
  trialStartDate: string | null;
  trialEndDate: string | null;
  /** An admitted child's first day; kept once they are enrolled. */
  startDate: string | null;
  googleEmail: string | null;
  personId: string | null;
  /** Short alternate door-panel key; null for everyone whose personId fits the panel. */
  doorId: string | null;
  allergies: StudentAllergy[];
  dietaryNote: string | null;
  allergySeverity: AllergySeverity;
  nationalities: { code: string; name: string; flag: string }[];
  passportOrThaiId: string | null;
  canShareToSocial: boolean;
  socialNote: string | null;
  /** Their parents have signed the form letting them leave campus unaccompanied. A standing
   *  permission, not a plan — whether they walk home on a given day is decided at the door. */
  canLeaveAlone: boolean;
  /** What is on that form, typically who signed it and when. Free text; never used for logic. */
  leaveAloneNote: string | null;
  createdAt: string;
  updatedAt: string;
  states: StudentState[];
};

export type StudentSortKey = 'name' | 'grade' | 'house' | 'status';

export type StudentListResponse = {
  data: Student[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type StudentsFilters = {
  page?: number;
  limit?: number;
  houseId?: string;
  gradeLevel?: number;
  active?: boolean;
  status?: StudentStatusFilter;
  search?: string;
  sortBy?: StudentSortKey;
  sortDir?: 'asc' | 'desc';
};

export type EnrolDraftCharge = TuitionCharge & { schoolYearId: string; semester: 1 | 2 };

/** `draftCharge` / `draftError` come back only when a draft invoice was asked for. */
export type EnrolStudentResponse = Student & {
  draftCharge?: EnrolDraftCharge | { skipped: 'outside-report-periods' };
  draftError?: string;
};

export type StudentAuditIssue = 'no-parents' | 'parents-never-logged-in' | 'missing-logins';

export type StudentAuditPlatform = 'hmh' | 'mcgrawhill' | 'ixl' | 'boddle' | 'locker' | 'wifi';

export type StudentAuditRow = {
  id: string;
  fullName: string;
  nickname: string | null;
  gradeLevel: number | null;
  houseId: string | null;
  parentCount: number;
  anyParentLoggedIn: boolean;
  /** False below Grade 1 — email/credential gaps are not flagged for them. */
  loginsRequired: boolean;
  /** What this student's grade is actually asked for; empty below Kindergarten. */
  requiredPlatforms: StudentAuditPlatform[];
  googleRequired: boolean;
  hasGoogle: boolean;
  googleEmail: string | null;
  platforms: Record<StudentAuditPlatform, boolean>;
  issues: StudentAuditIssue[];
};

export type StudentAuditSummary = {
  totalStudents: number;
  noParents: number;
  parentsNeverLoggedIn: number;
  missingLogins: number;
  withAnyIssue: number;
};

export type StudentAuditResponse = {
  summary: StudentAuditSummary;
  students: StudentAuditRow[];
};

export const studentsApi = {
  audit: () => fetchApi<StudentAuditResponse>(`${STUDENTS_PATH}/audit`),
  list: (filters: StudentsFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.houseId) params.append('houseId', filters.houseId);
    if (filters.gradeLevel !== undefined) params.append('gradeLevel', String(filters.gradeLevel));
    if (filters.active !== undefined) params.append('active', String(filters.active));
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDir) params.append('sortDir', filters.sortDir);
    const qs = params.toString();
    return fetchApi<StudentListResponse>(`${STUDENTS_PATH}${qs ? `?${qs}` : ''}`);
  },
  listAll: (filters: Omit<StudentsFilters, 'page' | 'limit'> = {}) => {
    const params = new URLSearchParams();
    if (filters.houseId) params.append('houseId', filters.houseId);
    if (filters.gradeLevel !== undefined) params.append('gradeLevel', String(filters.gradeLevel));
    if (filters.active !== undefined) params.append('active', String(filters.active));
    const qs = params.toString();
    return fetchApi<Student[]>(`${STUDENTS_PATH}/all${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string) => fetchApi<Student>(`${STUDENTS_PATH}/${id}`),
  create: (data: {
    fullName: string;
    nickname?: string;
    sex?: Sex;
    gradeLevel: number;
    edpFreePlaces?: number;
    houseId?: string | null;
    dateOfBirth?: string | null;
    photoUrl?: string;
    enrolmentStatus?: Exclude<EnrolmentStatus, 'left'>;
    trialStartDate?: string;
    trialEndDate?: string;
    startDate?: string;
    personId?: string;
    passportOrThaiId?: string;
  }) => fetchApi<Student>(STUDENTS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (
    id: string,
    data: Partial<{
      fullName: string;
      nickname: string | null;
      sex: Sex | null;
      gradeLevel: number;
      edpFreePlaces: number;
      houseId: string | null;
      dateOfBirth: string | null;
      photoUrl: string;
      personId: string | null;
      /** A digits-only string assigns; null clears. */
      doorId: string | null;
      passportOrThaiId: string | null;
      dietaryNote: string | null;
      allergies: { label: string; severe: boolean }[];
      canShareToSocial: boolean;
      socialNote: string | null;
      canLeaveAlone: boolean;
      leaveAloneNote: string | null;
      nationalities: string[];
    }>
  ) => fetchApi<Student>(`${STUDENTS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`${STUDENTS_PATH}/${id}`, { method: 'DELETE' }),
  startTrial: (id: string, data: { trialStartDate: string; trialEndDate: string }) =>
    fetchApi<Student>(`${STUDENTS_PATH}/${id}/trial`, { method: 'POST', body: JSON.stringify(data) }),
  extendTrial: (id: string, trialEndDate: string) =>
    fetchApi<Student>(`${STUDENTS_PATH}/${id}/trial/extend`, {
      method: 'POST',
      body: JSON.stringify({ trialEndDate }),
    }),
  enrol: (id: string, data: { startDate: string; gradeLevel?: number; createDraftInvoice?: boolean }) =>
    fetchApi<EnrolStudentResponse>(`${STUDENTS_PATH}/${id}/enrol`, { method: 'POST', body: JSON.stringify(data) }),
  changeStartDate: (id: string, startDate: string) =>
    fetchApi<Student>(`${STUDENTS_PATH}/${id}/start-date`, { method: 'POST', body: JSON.stringify({ startDate }) }),
  markLeft: (id: string) => fetchApi<Student>(`${STUDENTS_PATH}/${id}/left`, { method: 'POST' }),
  uploadPhoto: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    return fetchApi<Student>(`${STUDENTS_PATH}/${id}/photo`, { method: 'POST', body: fd });
  },
  setGoogleEmail: (id: string, email: string) =>
    fetchApi<Student>(`${STUDENTS_PATH}/${id}/google-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  clearGoogleEmail: (id: string) => fetchApi<Student>(`${STUDENTS_PATH}/${id}/google-email`, { method: 'DELETE' }),
};
